import React, { createContext, useContext, useState, useEffect, ReactNode } from 'react';
import { ThemeProvider as MuiThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';

// Extend MUI theme palette for debug panel colors
declare module '@mui/material/styles' {
  interface Palette {
    debug?: {
      panel: string;
      header: string;
      accent: string;
      border: string;
    };
  }
  interface PaletteOptions {
    debug?: {
      panel: string;
      header: string;
      accent: string;
      border: string;
    };
  }
}

type ThemeMode = 'light' | 'dark';

interface ThemeContextType {
  mode: ThemeMode;
  toggleTheme: () => void;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme must be used within ThemeProvider');
  }
  return context;
};

interface AIAvatarThemeProviderProps {
  children: ReactNode;
}

export const AIAvatarThemeProvider: React.FC<AIAvatarThemeProviderProps> = ({ children }) => {
  const [mode, setMode] = useState<ThemeMode>('light');

  useEffect(() => {
    // Get user roles to determine default theme
    const getUserRoles = (): string[] => {
      try {
        const userToken = localStorage.getItem('user_token');
        if (userToken) {
          const user = JSON.parse(userToken);
          const roles = user?.roles || [];
          if (Array.isArray(roles) && roles.length > 0) {
            return roles;
          }
        }
      } catch (error) {
        console.error('Error parsing user token:', error);
      }
      return [];
    };

    // Check for saved theme preference - check both app-theme and ai-avatar-theme for compatibility
    const appTheme = localStorage.getItem('app-theme');
    const savedTheme = localStorage.getItem('ai-avatar-theme') as ThemeMode;
    const userRoles = getUserRoles();
    const isDealershipExecutive = userRoles.includes('dealership_executive');
    const isBetaPerformanceManager = userRoles.includes('beta_maiq_performance_manager');
    const isDealershipGroupExecutive = userRoles.includes('dealership_group_executive');
    const isPerformanceManager = userRoles.includes('maiq_performance_manager');
    const isContentManager = userRoles.includes('maiq_content_manager');
    
    // Prioritize app-theme, but only use if it's 'light' or 'dark'
    if (appTheme && ['light', 'dark'].includes(appTheme)) {
      setMode(appTheme as ThemeMode);
      // Sync ai-avatar-theme
      localStorage.setItem('ai-avatar-theme', appTheme);
    } else if (savedTheme) {
      setMode(savedTheme);
      // Sync app-theme
      localStorage.setItem('app-theme', savedTheme);
    } else {
      // Set default theme based on user role
      // dealership_executive: default to 'dark'
      // beta_maiq_performance_manager, dealership_group_executive, maiq_performance_manager, maiq_content_manager: default to 'light'
      // Priority: dealership_executive > others
      if (isDealershipExecutive) {
        setMode('dark');
        localStorage.setItem('app-theme', 'dark');
        localStorage.setItem('ai-avatar-theme', 'dark');
      } else if (isBetaPerformanceManager || isDealershipGroupExecutive || isPerformanceManager || isContentManager) {
        setMode('light');
        localStorage.setItem('app-theme', 'light');
        localStorage.setItem('ai-avatar-theme', 'light');
      }
    }
  }, []);

  const toggleTheme = () => {
    setMode((prevMode) => {
      const newMode = prevMode === 'light' ? 'dark' : 'light';
      // Update both theme keys for consistency
      localStorage.setItem('ai-avatar-theme', newMode);
      localStorage.setItem('app-theme', newMode);
      return newMode;
    });
  };

  // Create MUI theme based on mode - matching Tailwind CSS colors exactly
  const theme = createTheme({
    palette: {
      mode,
      ...(mode === 'light'
        ? {
            // Light mode - matching Tailwind globals.css
            background: {
              default: 'rgb(255, 255, 255)', // oklch(1 0 0)
              paper: 'rgb(255, 255, 255)',
            },
            text: {
              primary: 'rgb(23, 23, 23)', // oklch(0.145 0 0)
              secondary: 'rgb(115, 115, 115)', // oklch(0.556 0 0)
            },
            primary: {
              main: 'rgb(35, 35, 35)', // oklch(0.205 0 0)
              contrastText: 'rgb(250, 250, 250)', // oklch(0.985 0 0)
            },
            secondary: {
              main: 'rgb(245, 245, 245)', // oklch(0.97 0 0)
              contrastText: 'rgb(35, 35, 35)',
            },
            divider: 'rgb(229, 229, 229)', // oklch(0.922 0 0)
            action: {
              hover: 'rgba(245, 245, 245, 0.8)',
            },
            // Debug Panel specific colors
            debug: {
              panel: 'rgb(250, 250, 250)',
              header: 'rgb(35, 35, 35)',
              accent: 'rgb(35, 35, 35)',
              border: 'rgb(229, 229, 229)',
            },
          }
        : {
            // Dark mode - matching Tailwind globals.css
            background: {
              default: 'rgb(23, 23, 23)', // oklch(0.145 0 0)
              paper: 'rgb(23, 23, 23)',
            },
            text: {
              primary: 'rgb(250, 250, 250)', // oklch(0.985 0 0)
              secondary: 'rgb(163, 163, 163)', // oklch(0.708 0 0)
            },
            primary: {
              main: 'rgb(250, 250, 250)', // oklch(0.985 0 0)
              contrastText: 'rgb(35, 35, 35)',
            },
            secondary: {
              main: 'rgb(64, 64, 64)', // oklch(0.269 0 0)
              contrastText: 'rgb(250, 250, 250)',
            },
            divider: 'rgb(64, 64, 64)', // oklch(0.269 0 0)
            action: {
              hover: 'rgba(64, 64, 64, 0.8)',
            },
            // Debug Panel specific colors (dark mode)
            debug: {
              panel: 'rgb(30, 30, 30)',
              header: 'rgb(45, 45, 48)',
              accent: 'rgb(78, 201, 176)', // DevTools teal
              border: 'rgb(60, 60, 60)',
            },
          }),
    },
    shadows: [
      'none',                                                                    // 0
      '0px 1px 3px rgba(0,0,0,0.12), 0px 1px 2px rgba(0,0,0,0.24)',           // 1
      '0px 3px 6px rgba(0,0,0,0.16), 0px 3px 6px rgba(0,0,0,0.23)',           // 2
      '0px 10px 16px rgba(0, 0, 0, 0.11), 0px 6px 6px rgba(0, 0, 0, 0.062)',         // 3
      '0px 14px 28px rgba(0,0,0,0.25), 0px 10px 10px rgba(0,0,0,0.22)',       // 4
      '0px 19px 38px rgba(0,0,0,0.30), 0px 15px 12px rgba(0,0,0,0.22)',       // 5
      '0px 25px 50px rgba(0,0,0,0.25)',                                        // 6
      '0px 30px 60px rgba(0,0,0,0.30)',                                        // 7
      '0px 8px 24px rgba(0,0,0,0.15)',                                         // 8 - Custom shadow for HomePageInterface
      '0px 12px 32px rgba(0,0,0,0.18)',                                        // 9
      '0px 16px 40px rgba(0,0,0,0.20)',                                        // 10
      '0px 20px 48px rgba(0,0,0,0.22)',                                        // 11
      '0px 24px 56px rgba(0,0,0,0.24)',                                        // 12
      '0px 28px 64px rgba(0,0,0,0.26)',                                        // 13
      '0px 32px 72px rgba(0,0,0,0.28)',                                        // 14
      '0px 36px 80px rgba(0,0,0,0.30)',                                        // 15
      '0px 40px 88px rgba(0,0,0,0.32)',                                        // 16
      '0px 44px 96px rgba(0,0,0,0.34)',                                        // 17
      '0px 48px 104px rgba(0,0,0,0.36)',                                       // 18
      '0px 52px 112px rgba(0,0,0,0.38)',                                       // 19
      '0px 56px 120px rgba(0,0,0,0.40)',                                       // 20
      '0px 60px 128px rgba(0,0,0,0.42)',                                       // 21
      '0px 64px 136px rgba(0,0,0,0.44)',                                       // 22
      '0px 68px 144px rgba(0,0,0,0.46)',                                       // 23
      '0px 72px 152px rgba(0,0,0,0.48)',                                       // 24
    ],
    shape: {
      borderRadius: 10, // 0.625rem = 10px
    },
    typography: {
      fontFamily: 'var(--font-sans, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif)',
      h1: {
        fontSize: '2.25rem', // text-4xl
        fontWeight: 600,
        lineHeight: 1.2,
      },
      h2: {
        fontSize: '1.875rem', // text-3xl
        fontWeight: 700,
        lineHeight: 1.3,
      },
      h3: {
        fontSize: '1.5rem', // text-2xl
        fontWeight: 600,
        lineHeight: 1.3,
      },
      body1: {
        fontSize: '0.875rem', // text-sm
        lineHeight: 1.6,
      },
      body2: {
        fontSize: '0.75rem', // text-xs
        lineHeight: 1.5,
      },
    },
    components: {
      MuiCssBaseline: {
        styleOverrides: {
          body: {
            margin: 0,
            padding: 0,
            overflow: 'hidden',
          },
        },
      },
      MuiPaper: {
        styleOverrides: {
          root: {
            backgroundImage: 'none',
          },
        },
      },
      MuiButton: {
        styleOverrides: {
          root: {
            textTransform: 'none',
            fontWeight: 500,
            borderRadius: '0.5rem',
            transition: 'all 0.2s ease-in-out',
          },
          containedPrimary: {
            '&:hover': {
              opacity: 0.9,
            },
          },
        },
      },
    },
  });

  return (
    <ThemeContext.Provider value={{ mode, toggleTheme }}>
      <MuiThemeProvider theme={theme}>
        <CssBaseline />
        {children}
      </MuiThemeProvider>
    </ThemeContext.Provider>
  );
};


